<template>
  <div class="asset-groups-page">
    <div class="asset-groups-header">
      <div>
        <h3 class="mb-1">
          {{ $t('aside.menu.pages.assetGroups') }}
        </h3>
        <div class="text-muted small">
          {{ $t('components.assetGroups.subtitle') }}
        </div>
      </div>
      <div class="d-flex align-items-center gap-3">
        <button
          v-if="activeTab === 'list' && canCreate"
          class="btn btn-primary d-flex align-items-center gap-2"
          @click="openCreateModal"
        >
          <i class="bi bi-plus-lg" />
          <span>{{ $t('components.assetGroups.addButton') }}</span>
        </button>
        <div class="tabs">
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'list' }"
            @click="activeTab = 'list'"
          >
            {{ $t('components.assetGroups.listTab') }}
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'hierarchy' }"
            @click="activeTab = 'hierarchy'"
          >
            {{ $t('components.assetGroups.hierarchyTab') }}
          </button>
        </div>
      </div>
    </div>

    <div class="tab-content mt-4">
      <div v-if="activeTab === 'list'">
        <div
          v-if="listError"
          class="alert alert-danger d-flex align-items-center gap-2 mb-3"
        >
          <i class="bi bi-exclamation-triangle-fill" />
          <span>{{ listError }}</span>
        </div>
        <div
          v-if="listLoading"
          class="d-flex align-items-center gap-3 py-3"
        >
          <span
            class="spinner-border text-primary"
            role="status"
          />
          <span>{{ $t('components.assetGroups.loading') }}</span>
        </div>
        <div class="data-grid-card">
          <DxDataGrid
            ref="gridRef"
            class="ag-grid"
            :data-source="dataSource"
            :remote-operations="true"
            :hover-state-enabled="true"
            :column-auto-width="false"
            :show-borders="true"
            :row-alternation-enabled="true"
            :column-hiding-enabled="true"
            :word-wrap-enabled="true"
          >
            <DxHeaderFilter :visible="true" />
            <DxFilterRow :visible="true" />
            <DxPaging :page-size="20" />
            <DxPager
              :allowed-page-sizes="[10, 20, 50]"
              :show-info="true"
              :show-page-size-selector="true"
              :show-navigation-buttons="true"
            />

            <DxColumn
              data-field="name"
              :caption="$t('components.assetGroups.columns.name')"
              :min-width="180"
            />
            <DxColumn
              data-field="hierarchyName"
              :caption="$t('components.assetGroups.columns.level')"
              :min-width="140"
              :allow-filtering="false"
              :hiding-priority="3"
            />
            <DxColumn
              data-field="parentName"
              :caption="$t('components.assetGroups.columns.parent')"
              :min-width="160"
              :allow-filtering="false"
              :hiding-priority="2"
            />
            <DxColumn
              data-field="depth"
              :caption="$t('components.assetGroups.columns.depth')"
              alignment="center"
              width="100"
              :allow-filtering="false"
              :hiding-priority="4"
            />
            <DxColumn
              data-field="usersCount"
              :caption="$t('components.assetGroups.columns.users')"
              alignment="center"
              width="120"
              :allow-filtering="false"
              :hiding-priority="1"
            />
            <DxColumn
              data-field="assetsCount"
              :caption="$t('components.assetGroups.columns.assets')"
              alignment="center"
              width="120"
              :allow-filtering="false"
              :hiding-priority="0"
            />
            <DxColumn
              data-field="vehiclesCount"
              :caption="$t('components.assetGroups.columns.vehicles')"
              alignment="center"
              width="120"
              :allow-filtering="false"
              :hiding-priority="1"
            />
            <DxColumn
              :caption="$t('components.assetGroups.columns.actions')"
              :width="150"
              :allow-filtering="false"
              :allow-sorting="false"
              cell-template="actions-template"
            />

            <template #actions-template="{ data }">
              <div class="action-buttons">
                <button
                  v-if="!isReadOnly"
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  :title="$t('components.assetGroups.actions.edit')"
                  @click="openEditModal(data.data)"
                >
                  <i class="bi bi-pencil" />
                </button>
                <button
                  v-if="canEdit"
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  :title="$t('components.assetGroups.actions.delete')"
                  @click="confirmDelete(data.data)"
                >
                  <i class="bi bi-trash" />
                </button>
                <button
                  v-if="isReadOnly"
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  :title="$t('components.assetGroups.actions.view')"
                  @click="openViewModal(data.data)"
                >
                  <i class="bi bi-eye" />
                </button>
              </div>
            </template>
          </DxDataGrid>
          <div
            v-if="isGridEmpty"
            class="text-center text-muted py-3"
          >
            {{ $t('components.assetGroups.empty') }}
          </div>
        </div>
      </div>
      <div v-else>
        <AssetHierarchyView ref="hierarchyView" />
      </div>
    </div>

    <!-- Asset Group Form Modal -->
    <AssetGroupFormModal
      ref="formModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onMounted, getCurrentInstance, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useQuasar } from 'quasar'
import AssetHierarchyView from '@/views/AssetHierarchyView.vue'
import AssetGroupFormModal from '@/components/content/_modals/AssetGroupFormModal.vue'
import {
  DxDataGrid,
  DxColumn,
  DxFilterRow,
  DxHeaderFilter,
  DxPaging,
  DxPager
} from 'devextreme-vue/data-grid'
import CustomStore from 'devextreme/data/custom_store'
import { getAssetGroups, getAssetGroup, deleteAssetGroup } from '@/crud/asset-group'
import { canCreateAssetGroups, canEditAssetGroups, isRestrictedAssetUser } from '@/utils/tools'

type TreeNode = {
  id: number
  name: string
  parentId: number | null
  parent?: { id: number; name: string } | null
  parentName?: string | null
  hierarchyName?: string | null
  depth?: number
  isActive?: boolean
  code?: string | null
  description?: string | null
  assetsTotal?: number
  assets?: Array<{ id: number; name?: string | null }>
  location?: {
    street?: string | null
    city?: string | null
    postalCode?: string | null
    country?: string | null
    countryCode?: string | null
    lat?: string | null
    lng?: string | null
  } | null
  users?: Array<{ id: number; email?: string; roles?: string[]; name?: string; surname?: string }>
  children?: TreeNode[]
}

type FlatRow = {
  id: number
  name: string
  hierarchyName?: string | null
  parentName?: string | null
  parent?: { id: number; name: string } | null
  depth?: number
  usersCount: number
  assetsCount: number
  vehiclesCount: number
  isActive?: boolean
  code?: string | null
  description?: string | null
  location?: TreeNode['location']
  users?: TreeNode['users']
  rawData?: TreeNode
}

export default defineComponent({
  name: 'AssetGroupsView',
  components: {
    AssetHierarchyView,
    AssetGroupFormModal,
    DxDataGrid,
    DxColumn,
    DxFilterRow,
    DxHeaderFilter,
    DxPaging,
    DxPager
  },
  emits: ['pageName'],
  setup(props, context) {
    const { t, locale } = useI18n()
    const vm = getCurrentInstance()
    const toast = vm?.proxy?.$toast
    const quasar = useQuasar()
    const activeTab = ref<'list' | 'hierarchy'>('list')
    const listLoading = ref(false)
    const listError = ref<string | null>(null)
    const formModal = ref<InstanceType<typeof AssetGroupFormModal> | null>(null)
    const hierarchyView = ref<InstanceType<typeof AssetHierarchyView> | null>(null)
    const gridRef = ref()
    const treeData = ref<TreeNode[]>([])
    const isGridEmpty = computed(() => {
      const inst = gridRef.value?.instance
      return !listLoading.value && !listError.value && inst && inst.totalCount() === 0
    })

    // Permission checks
    const canCreate = computed(() => canCreateAssetGroups())
    const canEdit = computed(() => canEditAssetGroups())
    const isReadOnly = computed(() => isRestrictedAssetUser())

    const setTitle = () => context.emit('pageName', t('aside.menu.pages.assetGroups'))
    setTitle()
    watch(locale, setTitle)

    const buildSearchValue = (filter: any): string => {
      if (!filter) return ''

      const extract = (f: any): string => {
        if (Array.isArray(f) && f.length >= 3 && typeof f[0] === 'string' && f[0] === 'name') {
          return f[2] || ''
        }
        return ''
      }

      const gather = (f: any): string => {
        if (!f) return ''
        if (typeof f[0] === 'string') {
          return extract(f)
        }
        if (Array.isArray(f)) {
          for (const item of f) {
            const val = gather(item)
            if (val) return val
          }
        }
        return ''
      }

      return gather(filter)
    }

    const dataSource = new CustomStore({
      cacheRawData: false,
      key: 'id',
      load: async (loadOptions) => {
        const params: any = {
          skip: loadOptions.skip ?? 0,
          take: loadOptions.take ?? 20
        }

        const searchValue = buildSearchValue(loadOptions.filter)
        if (searchValue) params.searchValue = searchValue

        if (loadOptions.sort) {
          params.sort = JSON.stringify(loadOptions.sort)
        }
        if (loadOptions.filter) {
          params.filter = JSON.stringify(loadOptions.filter)
        }

        try {
          listLoading.value = true
          listError.value = null

          const response = await getAssetGroups(params)
          const payload = response.data?.data || {}
          const rawRows: any[] = Array.isArray(payload) ? payload : (payload.result || payload.data || [])

          const rows = (rawRows || []).map((g: any) => ({
            id: g.id,
            name: g.name,
            hierarchyName: g.hierarchyName || g.hierarchy_name || null,
            parentName: g.parent?.name || g.parentName || null,
            parent: g.parent ? { id: g.parent.id, name: g.parent.name } : g.parentId ? { id: g.parentId, name: g.parentName } : null,
            depth: g.depth ?? 0,
            usersCount: g.usersCount ?? g.users?.length ?? g.assetGroupUsers?.length ?? 0,
            assetsCount: g.assetsCount ?? g.assets?.length ?? 0,
            vehiclesCount: g.vehiclesCount ?? g.vehicles?.length ?? 0,
            isActive: g.isActive ?? g.is_active ?? true,
            code: g.code,
            description: g.description,
            location: g.location,
            users: g.users,
            rawData: g
          }))

          const total = response.data?.totalCount ?? payload.count ?? 0
          return { data: rows, totalCount: total }
        } catch (e: any) {
          listError.value = e?.response?.data?.error || e?.message || t('components.assetGroups.error')
          throw e
        } finally {
          listLoading.value = false
        }
      }
    })

    const openCreateModal = () => {
      formModal.value?.openModal()
    }

    const openEditModal = async (row: FlatRow) => {
      try {
        const response = await getAssetGroup(row.id)
        const groupData = response.data.group
        const users = response.data.users || []
        const assets = response.data.assets || []
        const vehicles = response.data.vehicles || []

        formModal.value?.openModal({
          ...groupData,
          users,
          assets,
          vehicles
        })
      } catch (e: any) {
        listError.value = e?.response?.data?.error || e?.message || t('components.assetGroups.error')
      }
    }

    const openViewModal = async (row: FlatRow) => {
      try {
        const response = await getAssetGroup(row.id)
        const groupData = response.data.group
        const users = response.data.users || []
        const assets = response.data.assets || []
        const vehicles = response.data.vehicles || []

        formModal.value?.openModal({
          ...groupData,
          users,
          assets,
          vehicles
        }, true) // Pass true as second parameter for read-only mode
      } catch (e: any) {
        listError.value = e?.response?.data?.error || e?.message || t('components.assetGroups.error')
      }
    }

    const confirmDelete = async (row: FlatRow) => {
      quasar.dialog({
        title: t('components.assetGroups.actions.deleteTitle'),
        message: t('components.assetGroups.actions.confirmDelete'),
        cancel: true
      }).onOk(async () => {
        try {
          await deleteAssetGroup(row.id)

          toast?.success(t('components.assetGroups.actions.deleteSuccess'))

          gridRef.value?.instance?.refresh()

          if (activeTab.value === 'hierarchy' && hierarchyView.value) {
            hierarchyView.value.fetchTree()
          }
        } catch (e: any) {
          const errorMsg = e?.response?.data?.error || e?.message || t('components.assetGroups.actions.deleteError')
          listError.value = errorMsg
          toast?.error(errorMsg)
        }
      })
    }

    const handleSaved = async () => {
      gridRef.value?.instance?.refresh()

      if (activeTab.value === 'hierarchy' && hierarchyView.value) {
        hierarchyView.value.fetchTree()
      }
    }

    watch(
      () => activeTab.value,
      (val) => {
        if (val === 'hierarchy' && hierarchyView.value) {
          hierarchyView.value.fetchTree()
        }
      },
      { immediate: true }
    )

    onMounted(() => {
      if (activeTab.value === 'hierarchy' && hierarchyView.value) {
        hierarchyView.value.fetchTree()
      }
    })

    return {
      activeTab,
      listLoading,
      listError,
      formModal,
      hierarchyView,
      gridRef,
      dataSource,
      isGridEmpty,
      canCreate,
      canEdit,
      isReadOnly,
      openCreateModal,
      openEditModal,
      openViewModal,
      confirmDelete,
      handleSaved
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/asset-groups-view.scss';
</style>
